import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Order, OrderStatus, DefaultService } from "@/api/SolarCloudApi";
import { doFetch } from "@/util/fetch";
import { apiServer } from "@/util/apiServer";
import { toast } from "sonner";
import { useBuyPanelsStore } from "../store";
import { getIdToken } from "@/util/authentication";
import { useRef } from "react";
import paypalLogo from "@/res/logos/PayPal_Logo_2022/PayPal_Logo_Horizontal_Full_Color_RGB.png";

interface PayPalPaymentFormProps {
  order: Order;
  onSuccess: (order: Order) => void;
  isSubmitting: boolean;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  payIn4?: boolean;
  beforePayment?: () => Promise<boolean>;
}

/**
 * PayPalPaymentForm component for SolarCloud Buy Panels flow.
 * Integrates with Backend-ORDER_KB flows for order submission and PayPal capture.
 */
export default function PayPalPaymentForm({
  order,
  onSuccess,
  isSubmitting,
  setIsSubmitting,
  payIn4,
  beforePayment,
}: PayPalPaymentFormProps) {
  const submittedRef = useRef(false);

  async function onClick(_data: Record<string, unknown>, actions: { resolve: () => Promise<void>; reject: () => Promise<void> }) {
    const canProceed = beforePayment ? await beforePayment() : true;

    if (!canProceed) {
      setIsSubmitting?.(false);
      return actions.reject();
    }

    return actions.resolve();
  }

  /**
   * 1. Submits the order to backend if it's still in DRAFT.
   * 2. Calls the backend create endpoint to get a PayPal Order ID.
   */
  async function createOrder() {
    try {
      // Ensure order is submitted before creating a PayPal order
      if (order.status !== OrderStatus.SUBMITTED) {
        try {
          await DefaultService.newOrderSubmit(order.uuid!);
          submittedRef.current = true;
        } catch (err) {
          console.error("Failed to submit order before PayPal create:", err);
          toast.error("Failed to submit order. Please review your order and try again.");
          throw err;
        }
      }

      const token = await getIdToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      // Calling the fixed backend endpoint that handles the PayPal Access Token
      const response = await doFetch(`${apiServer ?? ""}/api/orders/new/${order.uuid}/action/payment/paypal/create`, {
        method: "POST",
        headers,
      });

      if (!response.ok) {
        const content = await (async () => {
          try {
            return await response.json();
          } catch (e) {
            return await response.text();
          }
        })();

        console.error("createOrder failed", { status: response.status, body: content });

        if (response.status === 401) {
          toast.error("You must be signed in to make a payment.");
        } else {
          const msg = typeof content === "object" ? content.message || JSON.stringify(content) : String(content);
          toast.error(`Failed to initialize PayPal: ${msg}`);
        }

        // Revert submission if initialization fails
        if (submittedRef.current) {
          try {
            await DefaultService.newOrderCheckoutCancel(order.uuid!);
            submittedRef.current = false;
          } catch (cancelErr) {
            console.error("Failed to cancel order after creation failure", cancelErr);
          }
        }

        throw new Error("Create order failed");
      }

      const data = await response.json();
      // Backend returns `id` (PreparePaymentResponse.id). Fall back to `orderID` if present for compatibility.
      return (data.id ?? data.orderID) as string;
    } catch (err) {
      console.error("createOrder error:", err);
      throw err;
    }
  }

  /**
   * Finalizes the payment by capturing the order on the backend.
   */
  async function onApprove(data: any) {
    try {
      setIsSubmitting?.(true);

      const token = await getIdToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await doFetch(`${apiServer ?? ""}/api/orders/new/${order.uuid}/action/payment/paypal/capture`, {
        method: "POST",
        headers,
        body: JSON.stringify({ paymentIntentId: data.orderID }),
      });

      if (response.status !== 200) {
        const content = await (async () => {
          try { return await response.json(); } catch (e) { return await response.text(); }
        })();
        
        console.error("capture failed", { status: response.status, body: content });
        toast.error("Payment capture failed. Please try again.");
        throw new Error("Capture failed");
      }

      const updatedOrder = await response.json();
      toast.success("Payment successful");
      
      // Mark step complete in persisted store
      useBuyPanelsStore.getState().setStepComplete("confirmation");
      onSuccess(updatedOrder);
      
    } catch (err) {
      console.error("onApprove error:", err);
      toast.error("Payment finalization failed.");
    } finally {
      setIsSubmitting?.(false);
    }
  }

  function onError(err: any) {
    // Use console.error so the message stands out in logs and preserves stack traces
    console.error("PayPal SDK error:", err);
    // Show a helpful toast (include error message when available)
    const msg = err?.message || "PayPal is having trouble. Please try again or use another method.";
    toast.error(msg);
  }

  /**
   * Reverts the order if the user cancels the PayPal popup.
   */
  async function onCancel(data: any) {
    setIsSubmitting?.(false);

    if (submittedRef.current) {
      try {
        await DefaultService.newOrderCheckoutCancel(order.uuid!);
        submittedRef.current = false;
        toast("Payment cancelled; your order has been reverted to draft.");
      } catch (err) {
        console.error("Failed to cancel order after user cancellation:", err);
      }
    } else {
      toast("Payment was cancelled.");
    }
  }

  const [{ isPending, isRejected, isResolved }] = usePayPalScriptReducer();
  const paypalButtonContainerRef = useRef<HTMLDivElement | null>(null);

  if (isRejected) {
    // Log an explicit message to help debugging SDK load failures
    console.error(
      "PayPal script failed to load. Check the Network tab for the SDK URL (https://www.paypal.com/sdk/js?...), HTTP status code and response body."
    );
    if (process.env.NODE_ENV !== "production") {
      console.info(
        "Tip: Common causes are missing/invalid client-id or malformed query params. Verify env.paypal.clientId and the backend client token."
      );
    }

    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
        PayPal is currently unavailable. Please refresh or try another method.
      </div>
    );
  }

  if (isPending || !isResolved) {
    return (
      <div className="h-12 flex items-center justify-center animate-pulse text-slate-400">
        Syncing with PayPal…
      </div>
    );
  }

  const triggerPayPalButton = () => {
    try {
      const container = paypalButtonContainerRef.current;
      if (!container) return;
      const btn = container.querySelector("button");
      if (btn instanceof HTMLButtonElement) {
        btn.click();
        return;
      }
      console.warn("PayPal button not found inside container. Container HTML:", container.innerHTML);
    } catch (err) {
      console.error("Failed to trigger PayPal button programmatically", err);
    }
  };

  return (
    <div className="space-y-4">
      {payIn4 ? (
        <>
          <div className="relative w-full">
            <div
              ref={paypalButtonContainerRef}
              // overlay the SDK button over our visible button so clicks hit it directly
              style={{ position: "absolute", inset: 0, opacity: 0, zIndex: 20 }}
              aria-hidden={false}
            >
              <PayPalButtons
                style={{ layout: "vertical", shape: "rect", height: 45, color: "gold" }}
                onClick={onClick}
                createOrder={async () => await createOrder()}
                onApprove={async (data) => await onApprove(data)}
                onError={onError}
                onCancel={onCancel}
              />
            </div>

            <button
              type="button"
              // keep click handler as backup, but overlay will receive clicks in most browsers
              onClick={async () => {
                const canProceed = beforePayment ? await beforePayment() : true;
                if (!canProceed) {
                  return;
                }

                setIsSubmitting?.(true);
                triggerPayPalButton();
              }}
              className="w-full rounded-lg flex items-center justify-center gap-3 py-3 bg-[#33f0d9] hover:brightness-95 z-10 relative"
              aria-label="Pay in 4 via PayPal"
            >
              <img src={paypalLogo} alt="PayPal" className="h-6 object-contain" />
              <span className="text-white font-semibold text-lg">Pay in 4</span>
            </button>
          </div>
        </>
      ) : (
        <PayPalButtons
          style={{ layout: "vertical", shape: "rect", height: 45, color: "gold" }}
          onClick={onClick}
          createOrder={async () => await createOrder()}
          onApprove={async (data) => await onApprove(data)}
          onError={onError}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}